import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";

// IndexedDB setup for persistent caching
const DB_NAME = "IconikThumbnailCache";
const DB_VERSION = 1;
const STORE_NAME = "thumbnails";
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// In-memory cache for immediate access
const memoryCache = new Map();
let dbPromise = null;

// Initialize IndexedDB
const initDB = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      console.warn("IndexedDB not supported, falling back to memory cache only");
      resolve(null);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("Error opening IndexedDB:", request.error);
      resolve(null); // Don't reject, just fall back to memory cache
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }
    };
  });

  return dbPromise;
};

// Get from persistent cache
const getFromPersistentCache = async (key) => {
  try {
    const db = await initDB();
    if (!db) return null;

    return new Promise((resolve) => {
      const transaction = db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const data = request.result;
        if (data) {
          // Check if cache is expired
          if (Date.now() - data.timestamp < CACHE_EXPIRY) {
            memoryCache.set(key, data.url); // Update memory cache
            resolve(data.url);
          } else {
            // Remove expired entry
            removeFromPersistentCache(key);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error("Error reading from cache:", request.error);
        resolve(null);
      };
    });
  } catch (error) {
    console.error("Error accessing persistent cache:", error);
    return null;
  }
};

// Save to persistent cache
const saveToPersistentCache = async (key, url) => {
  try {
    const db = await initDB();
    if (!db) return;

    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    
    store.put({
      key,
      url,
      timestamp: Date.now()
    });

    // Clean up old entries periodically
    cleanupOldEntries(db);
  } catch (error) {
    console.error("Error saving to cache:", error);
  }
};

// Remove from persistent cache
const removeFromPersistentCache = async (key) => {
  try {
    const db = await initDB();
    if (!db) return;

    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    store.delete(key);
  } catch (error) {
    console.error("Error removing from cache:", error);
  }
};

// Clean up old cache entries
const cleanupOldEntries = async (db) => {
  try {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index("timestamp");
    const cutoffTime = Date.now() - CACHE_EXPIRY;
    
    const range = IDBKeyRange.upperBound(cutoffTime);
    const request = index.openCursor(range);

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        store.delete(cursor.primaryKey);
        cursor.continue();
      }
    };
  } catch (error) {
    console.error("Error cleaning up cache:", error);
  }
};

// Generate a thumbnail URL with size parameters (synchronous for immediate use)
export const getThumbnailUrl = (originalUrl, size = 200) => {
  if (!originalUrl) return null;

  // Generate cache key
  const cacheKey = `${originalUrl}_${size}`;
  
  // Check memory cache first
  if (memoryCache.has(cacheKey)) {
    return memoryCache.get(cacheKey);
  }

  // Generate thumbnail URL
  let thumbnailUrl = originalUrl;

  // For Firebase Storage URLs with flat structure
  if (originalUrl.includes("firebasestorage.googleapis.com")) {
    // Extract school ID and filename from URL
    const urlMatch = originalUrl.match(/schools%2F([^%]+)%2Ffiles%2F([^?]+)/);
    if (urlMatch) {
      const [, schoolId, fileName] = urlMatch;

      // First, try to use pre-generated thumbnail
      const decodedFileName = decodeURIComponent(fileName);
      const nameParts = decodedFileName.split(".");
      const ext = nameParts.pop();
      const baseName = nameParts.join(".");
      const thumbnailFileName = `${baseName}_thumb_${size}.${ext}`;

      // Build thumbnail URL
      const baseUrl = originalUrl.split("/o/")[0];
      const thumbnailPath = `schools%2F${schoolId}%2Fthumbnails%2F${encodeURIComponent(
        thumbnailFileName
      )}`;

      // Extract token from original URL
      const tokenMatch = originalUrl.match(/token=([^&]+)/);
      if (tokenMatch) {
        const token = tokenMatch[1];
        thumbnailUrl = `${baseUrl}/o/${thumbnailPath}?alt=media&token=${token}`;
      } else {
        // Fallback: Use Cloud Function for on-demand generation
        const functionUrl = `https://us-central1-iconik-portal.cloudfunctions.net/getImageWithSize`;
        thumbnailUrl = `${functionUrl}?school=${schoolId}&file=${decodedFileName}&w=${size}&h=${size}`;
      }
    }
  }

  // Cache the result
  memoryCache.set(cacheKey, thumbnailUrl);
  
  // Save to persistent cache asynchronously (non-blocking)
  saveToPersistentCache(cacheKey, thumbnailUrl).catch(console.error);
  
  // Also check persistent cache asynchronously for next time
  getFromPersistentCache(cacheKey).then(cachedUrl => {
    if (cachedUrl && cachedUrl !== thumbnailUrl) {
      memoryCache.set(cacheKey, cachedUrl);
    }
  }).catch(console.error);

  return thumbnailUrl;
};

// Create optimized thumbnail during upload
export const createOptimizedThumbnail = async (file, schoolId, sizes = [200, 400, 800]) => {
  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith("image/")) {
      resolve({ original: file, thumbnails: [] });
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = async () => {
        const thumbnails = [];

        try {
          // Generate multiple thumbnail sizes
          for (const maxSize of sizes) {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // Calculate dimensions maintaining aspect ratio
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
            }

            canvas.width = width;
            canvas.height = height;

            // Apply image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";

            // Draw thumbnail
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with optimization
            await new Promise((resolve) => {
              canvas.toBlob(
                async (blob) => {
                  if (blob) {
                    // Upload thumbnail
                    const thumbnailName = `thumb_${maxSize}_${file.name}`;
                    const thumbnailRef = ref(
                      storage,
                      `schools/${schoolId}/thumbnails/${thumbnailName}`
                    );
                    
                    try {
                      await uploadBytes(thumbnailRef, blob);
                      const thumbnailUrl = await getDownloadURL(thumbnailRef);
                      thumbnails.push({
                        size: maxSize,
                        url: thumbnailUrl,
                        blob: blob
                      });
                    } catch (error) {
                      console.error(`Error uploading ${maxSize}px thumbnail:`, error);
                    }
                  }
                  resolve();
                },
                "image/jpeg",
                0.85 // 85% quality for good balance
              );
            });
          }

          resolve({
            original: e.target.result,
            thumbnails: thumbnails,
            dimensions: { width: img.width, height: img.height }
          });
        } catch (error) {
          console.error("Error creating thumbnails:", error);
          resolve({ original: e.target.result, thumbnails: [] });
        }
      };

      img.onerror = () => {
        console.error("Error loading image for thumbnail generation");
        resolve({ original: file, thumbnails: [] });
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      console.error("Error reading file for thumbnail generation");
      resolve({ original: file, thumbnails: [] });
    };

    reader.readAsDataURL(file);
  });
};

// Compress image before upload
export const compressImage = async (file, maxSizeMB = 5) => {
  return new Promise((resolve) => {
    // Check if file needs compression
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size <= maxSizeBytes || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        // Reduce dimensions if image is very large
        const maxDimension = 4000; // Max 4000px on any side
        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Apply high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);

        // Start with high quality and reduce until file size is acceptable
        let quality = 0.9;
        const attemptCompression = () => {
          canvas.toBlob(
            (blob) => {
              if (blob.size > maxSizeBytes && quality > 0.3) {
                quality -= 0.1;
                attemptCompression();
              } else {
                // Create a new File object with the compressed blob
                const compressedFile = new File([blob], file.name, {
                  type: "image/jpeg",
                  lastModified: Date.now()
                });
                
                console.log(`Compressed image from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
                resolve(compressedFile);
              }
            },
            "image/jpeg",
            quality
          );
        };

        attemptCompression();
      };

      img.onerror = () => {
        console.error("Error loading image for compression");
        resolve(file);
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      console.error("Error reading file for compression");
      resolve(file);
    };

    reader.readAsDataURL(file);
  });
};

// Preload images in batches with optimizations
export const preloadImages = async (urls, batchSize = 5) => {
  const batches = [];

  for (let i = 0; i < urls.length; i += batchSize) {
    batches.push(urls.slice(i, i + batchSize));
  }

  return batches.reduce((promise, batch) => {
    return promise.then(() => {
      return Promise.all(
        batch.map(async (url) => {
          // Get thumbnail URL instead of original
          const thumbnailUrl = await getThumbnailUrl(url, 200);
          
          return new Promise((resolve) => {
            const img = new Image();
            img.loading = "lazy";
            img.decoding = "async";
            
            const timeout = setTimeout(() => {
              console.warn(`Image preload timeout: ${thumbnailUrl}`);
              resolve();
            }, 5000); // 5 second timeout

            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            
            img.onerror = () => {
              clearTimeout(timeout);
              console.warn(`Failed to preload: ${thumbnailUrl}`);
              resolve(); // Continue even if error
            };
            
            img.src = thumbnailUrl;
          });
        })
      );
    });
  }, Promise.resolve());
};

// Clear all caches
export const clearThumbnailCache = async () => {
  memoryCache.clear();
  
  try {
    const db = await initDB();
    if (db) {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      store.clear();
    }
  } catch (error) {
    console.error("Error clearing cache:", error);
  }
};

// Initialize cache on module load
initDB();